import type { Entity, EntityId } from "./Entity";
import type { EntityComponent, ComponentType } from "./components";
import type { Vec2 } from "./geometry";
import type { SceneDefinition } from "./SceneDefinition";
import type { EntityDefinitionRegistry } from "../game-content/EntityDefinitionRegistry";
import { defaultTransform } from "./geometry";

export interface EntityDiff {
  readonly position?: Vec2;
  readonly rotation?: number;
  readonly components?: readonly EntityComponent[];
  readonly visible?: boolean;
}

export interface SceneDiff {
  readonly modified: Readonly<Record<EntityId, EntityDiff>>;
  readonly removed: readonly EntityId[];
  readonly added: readonly Entity[];
}

const EMPTY_DIFF: SceneDiff = { modified: {}, removed: [], added: [] };

interface MutableEntityDiff {
  position?: Vec2;
  rotation?: number;
  components?: readonly EntityComponent[];
  visible?: boolean;
}

export function computeSceneDiff(
  currentEntities: readonly Entity[],
  initialEntities: readonly Entity[],
): SceneDiff {
  const initialMap = new Map<EntityId, Entity>();
  for (const e of initialEntities) initialMap.set(e.id, e);

  const modified: Record<string, EntityDiff> = {};
  const added: Entity[] = [];

  for (const entity of currentEntities) {
    const initial = initialMap.get(entity.id);
    if (!initial) {
      added.push(entity);
      continue;
    }

    const diff: MutableEntityDiff = {};

    if (entity.transform.position.x !== initial.transform.position.x ||
        entity.transform.position.y !== initial.transform.position.y) {
      diff.position = entity.transform.position;
    }

    if (entity.transform.rotation !== initial.transform.rotation) {
      diff.rotation = entity.transform.rotation;
    }

    if (componentsChanged(entity.components, initial.components)) {
      diff.components = entity.components;
    }

    if (entity.visible !== initial.visible) {
      diff.visible = entity.visible;
    }

    if (diff.position !== undefined || diff.rotation !== undefined ||
        diff.components !== undefined || diff.visible !== undefined) {
      (modified as Record<string, EntityDiff>)[entity.id] = diff;
    }
  }

  const removed: EntityId[] = [];
  for (const initial of initialEntities) {
    if (!currentEntities.some((e) => e.id === initial.id)) {
      removed.push(initial.id);
    }
  }

  if (Object.keys(modified).length === 0 && removed.length === 0 && added.length === 0) {
    return EMPTY_DIFF;
  }

  return { modified, removed, added };
}

function componentsChanged(
  current: readonly EntityComponent[],
  initial: readonly EntityComponent[],
): boolean {
  if (current.length !== initial.length) return true;
  const initialByType = new Map<ComponentType, EntityComponent>();
  for (const c of initial) initialByType.set(c.type, c);
  for (const c of current) {
    const orig = initialByType.get(c.type);
    if (!orig || JSON.stringify(c) !== JSON.stringify(orig)) return true;
  }
  return false;
}

export function applySceneDiff(
  entities: readonly Entity[],
  diff: SceneDiff,
): Entity[] {
  let result = entities.map((e) => {
    const d = diff.modified[e.id];
    if (!d) return e;
    return {
      ...e,
      transform: d.position !== undefined || d.rotation !== undefined
        ? { ...e.transform, ...("position" in d ? { position: d.position } : {}), ...("rotation" in d ? { rotation: d.rotation } : {}) }
        : e.transform,
      components: d.components ?? e.components,
      visible: d.visible ?? e.visible,
    };
  });

  if (diff.removed.length > 0) {
    const removedSet = new Set(diff.removed);
    result = result.filter((e) => !removedSet.has(e.id));
  }

  if (diff.added.length > 0) {
    result = [...result, ...diff.added];
  }

  return result;
}

export function buildSceneFromDefinition(
  definition: SceneDefinition,
  registry: EntityDefinitionRegistry,
): Entity[] {
  return definition.entities.map((def) => {
    const base = registry.get(def.definitionId);
    if (!base) {
      throw new Error(`No entity definition for "${def.definitionId}" (entity "${def.id}")`);
    }
    const kind = def.kind ?? base.kind;
    const components = mergeComponents(base.components, def.components ?? []);
    return {
      id: def.id,
      kind,
      definitionId: def.definitionId,
      transform: defaultTransform(def.position ?? { x: 0, y: 0 }, def.rotation ?? 0),
      components,
      visible: !def.hidden,
    };
  });
}

function mergeComponents(
  base: readonly EntityComponent[],
  overrides: readonly EntityComponent[],
): EntityComponent[] {
  if (overrides.length === 0) return [...base];
  const merged = new Map<ComponentType, EntityComponent>();
  for (const c of base) merged.set(c.type, c);
  for (const c of overrides) merged.set(c.type, c);
  return [...merged.values()];
}
